import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Container, Flex, Text } from '@chakra-ui/react';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import { useRouter } from 'next/router';
import { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { withCommonServerSideProps } from '@/lib/withCommonServerSideProps';
import { useToast } from '@/hooks/useToast';

import { H1 } from '@/components/Heading';
import NotFound from '@/components/NotFound';
import Seo from '@/components/Seo';
import { SortingProducts, SortOptionValue } from '@/components/Sorting/SortingProducts';
import { Button } from '@/components/UI/Buttons/Button';

import { ProductList } from '@/layouts/ProductsSection/ProductList';
import { Product } from '@/models/api';
import { CategoryProductListProps, Sort, SortDir } from '@/models/props/ProductCardProps';
import { ProductService } from '@/services';

type SearchPageSearchParams = {
    q?: string;
    sortDir?: SortDir;
    sort?: Sort;
};

type SearchPageProps = {
    phrase: string;
    session?: Session | null;
    searchParams?: SearchPageSearchParams;
} & CategoryProductListProps;

export const Search: NextPage<SearchPageProps> = (props) => {
    const session = useMemo(() => props.session, [props.session]);
    const intl = useIntl();
    const router = useRouter();
    const { errorToast } = useToast();
    const { searchParams, totalCount, currentPage, totalPageCount, items } = props;

    const [currentPageNo, setCurrentPageNo] = useState(currentPage || 1);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<Product[]>(items);
    const [phrase, setPhrase] = useState(props.phrase);
    const [sort, setSort] = useState<SortOptionValue>({
        sort: searchParams?.sort,
        sortDir: searchParams?.sortDir
    });

    const fetchResults = useCallback(
        async (page: number, sortDirection?: SortDir, sortBy?: Sort) => {
            try {
                return await ProductService.getProductsList({
                    text: phrase,
                    sort: sortBy,
                    sortDir: sortDirection,
                    pageNo: page,
                    opts: { session }
                });
            } catch (e) {
                errorToast({
                    title: intl.formatMessage({ id: 'page.search.fetch-results.error' })
                });
            }
        },
        [intl, phrase, errorToast, session]
    );

    const loadMoreHandler = useCallback(
        async (event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            setLoading(() => true);
            const nextPage = currentPageNo + 1;
            setCurrentPageNo(() => nextPage);
            const results = await fetchResults(nextPage, sort?.sortDir, sort?.sort);
            if (results) {
                setResults((prevState) => [...prevState, ...results.items]);
            }
            setLoading(() => false);
        },
        [currentPageNo, fetchResults, sort]
    );

    const sortChangeEventHandler = useCallback(
        async (value: SortOptionValue) => {
            setSort(() => value);

            setLoading(() => true);

            setCurrentPageNo(() => 1);
            const results = await fetchResults(1, value.sortDir, value.sort);
            if (results) {
                setResults(() => results.items);
            }
            setLoading(() => false);
        },
        [fetchResults]
    );

    useEffect(() => {
        const searchText = (router.query.q as string) || '';
        setPhrase(() => searchText);
    }, [router.query]);

    useEffect(() => {
        setResults(() => props.items);
    }, [props.items, setResults]);

    return (
        <>
            <Seo templateTitle={intl.formatMessage({ id: 'page.search.meta.title' })} />
            <Container maxW="container.3xl" px={8} pb={14} pt={8}>
                <Box textAlign="center">
                    <H1 textAlign="center" mb={3}>
                        <FormattedMessage id="page.search.title" />
                    </H1>
                    {phrase && (
                        <Box color="grey.900" fontSize={18}>
                            <FormattedMessage
                                id="search-results-for"
                                values={{
                                    search: phrase,
                                    'data-search-query': (value) => (
                                        <Text display="inline-block" fontStyle="italic">
                                            {value}
                                        </Text>
                                    )
                                }}
                            />
                        </Box>
                    )}
                </Box>
                <Flex
                    my={6}
                    flexDirection={{ base: 'column', md: 'row' }}
                    justifyContent="center"
                    alignItems="center"
                >
                    <SortingProducts selectValue={sortChangeEventHandler}></SortingProducts>
                    <Text textColor="grey.600" mt={{ base: 4, md: 0 }}>
                        <FormattedMessage id="total-count"></FormattedMessage>: {totalCount}
                    </Text>
                </Flex>

                <Box pt={6}>
                    {results?.length > 0 ? (
                        <ProductList items={results} />
                    ) : (
                        <NotFound message="page.search.results-not-found" />
                    )}
                </Box>
                {currentPageNo < totalPageCount && (
                    <>
                        <Flex justifyContent="center" pt={16}>
                            <Button
                                onClick={loadMoreHandler}
                                fontSize="xl"
                                h={20}
                                py={3}
                                px={12}
                                rounded="full"
                                rightIcon={<ChevronDownIcon />}
                                isLoading={loading}
                                spinnerPlacement="end"
                                loadingText={intl.formatMessage({ id: 'loading' })}
                            >
                                <FormattedMessage id="see-more-products"></FormattedMessage>
                            </Button>
                        </Flex>
                    </>
                )}
            </Container>
        </>
    );
};

export const getServerSideProps = withCommonServerSideProps(
    async (
        context: GetServerSidePropsContext
    ): Promise<GetServerSidePropsResult<SearchPageProps>> => {
        const session = await getSession({ req: context.req });
        const query = context.query as SearchPageSearchParams;

        const searchParams: SearchPageSearchParams = Object.fromEntries(new URLSearchParams(query));
        const phrase = searchParams.q || '';

        const productsResponse = await ProductService.getProductsList({
            text: phrase,
            sortDir: searchParams.sortDir,
            sort: searchParams.sort,
            opts: { session }
        });

        return {
            props: {
                searchParams,
                phrase,
                session,
                ...productsResponse
            }
        };
    }
);

export default Search;
